import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import {
  FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL_ACTION,
  fetchOpenJobsForRecentProfessional,
  showMessage,
} from '../../../actions';
import { clearSuccess } from '../../../actions/success';
import { createSuccessSelector } from '../../../api/selectors';
import { DotsMenu, MenuItem } from '../../../commonComponents/DotsMenu';
import { IconButton } from '../../../commonComponents/IconButton';
import history from '../../../browserHistory';
import shareIcon from '../../../images/share-icon.svg';
import { Colors } from '../../../themes/colors';
import { type InviteSource } from '../../../utils/posthog/inviteTracking';
import {
  fireWorkedWithInviteClicked,
  fireFavoritesInviteClicked,
  fireNewToYouInviteClicked,
  fireTodayShiftInviteClicked,
  fireExistingInviteClicked,
  fireNewInviteClicked,
  type TInviteDropdownSource,
  type TInviteProCategory,
} from '~/posthogs/events';
import { ShiftRequestRedesign } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { routes } from '~/routes';
import { SelectProfessionTypePopup } from '~/components/ShiftRequestRedesign/SelectProfessionType/SelectProfessionTypePopup';
import { useSelectProfessionType } from '~/components/ShiftRequestRedesign/SelectProfessionType/useSelectProfessionType';
import { IInviteButtonUser } from '../interfaces';

const actions = [FETCH_OPEN_JOBS_FOR_RECENT_PROFESSIONAL_ACTION];
const successSelector = createSuccessSelector(actions);

// InviteButton is used in Professional Hub and Dashboard contexts
type TInviteButtonSource = Extract<
  InviteSource,
  | 'favorites'
  | 'worked_with'
  | 'new_to_you'
  | 'dashboard'
  | 'shift_details'
  | 'shifts_open'
  | 'shifts_action_needed'
  | 'schedule_list'
>;

interface IInviteButtonProps {
  user: IInviteButtonUser;
  disabled?: boolean;
  listener?: () => void;
  showTooltip?: boolean;
  position?: 'left' | 'right';
  onValidate?: () => boolean;
  CustomMenuIcon?: ({ isHovered }: { isHovered: boolean }) => React.ReactNode;
  source?: TInviteButtonSource;
  shiftId?: string;
}

const sourceToDropdownSource: Record<string, TInviteDropdownSource> = {
  favorites: 'Professional Hub',
  worked_with: 'Professional Hub',
  new_to_you: 'Professional Hub',
  dashboard: 'Today',
};

const sourceToProCategory: Record<string, TInviteProCategory> = {
  favorites: 'Favorites',
  worked_with: 'Worked With',
  new_to_you: 'New to You',
};

export const InviteButton = ({
  user,
  disabled,
  listener,
  showTooltip = true,
  position = 'right',
  onValidate,
  CustomMenuIcon,
  source,
  shiftId,
}: IInviteButtonProps) => {
  const dispatch = useDispatch();
  const isSuccessRequest = useSelector((state) => successSelector(state));
  const { offers, professionalId } = useAppSelector((state) => state.job.recentOpenJobs);
  const selectedOffice = useAppSelector(
    (state) => state.user.selectedChildOffice ?? state.user.user,
  );
  const allowedRequest = selectedOffice?.allowedRequest || {};
  const isDisabled = disabled || !(allowedRequest as any)?.tempOrMdbShifts;

  const shouldUseShiftRequestRedesign = useFeatureFlag(ShiftRequestRedesign);
  const [showProfessionPopup, setShowProfessionPopup] = useState(false);
  const officeState = selectedOffice?.address?.[0]?.state ?? '';
  const { availableProfessions } = useSelectProfessionType({ officeState });

  const filteredProfessions = useMemo(() => {
    const proCodes = user?.professions || [];
    return availableProfessions.filter((p) => proCodes.includes(p.code));
  }, [availableProfessions, user?.professions]);

  const fireEntryPointEvent = useCallback(() => {
    if (source === 'dashboard') {
      fireTodayShiftInviteClicked({ shiftId: shiftId || '' });
    } else if (source === 'favorites') {
      fireFavoritesInviteClicked({ professionalId: user?.id });
    } else if (source === 'worked_with') {
      fireWorkedWithInviteClicked({ professionalId: user?.id });
    } else if (source === 'new_to_you') {
      fireNewToYouInviteClicked({ professionalId: user?.id });
    }
  }, [source, shiftId, user?.id]);

  const handleInviteToExistingShift = () => {
    if (source && sourceToDropdownSource[source] && sourceToProCategory[source]) {
      fireExistingInviteClicked({
        source: sourceToDropdownSource[source],
        proCategory: sourceToProCategory[source],
      });
    }

    dispatch(
      fetchOpenJobsForRecentProfessional({
        officeId: user?.officeId,
        professionalId: user?.id,
        profession: user?.profession,
        page: 1,
      }),
    );
  };

  const renderCustomMenuIcon = ({ isHovered }: { isHovered: boolean }) => {
    return CustomMenuIcon ? (
      <CustomMenuIcon isHovered={isHovered} />
    ) : (
      <IconButton
        buttonStyle={{
          ...styles.buttonStyle,
          backgroundColor: isDisabled ? Colors.primary_200 : Colors.primary_500,
        }}
        imgStyle={styles.imgStyle}
        src={shareIcon}
        tooltipContent={showTooltip ? 'Invite' : ''}
        disabled={isDisabled}
      />
    );
  };

  const getOrderedProfessionNames = useCallback(() => {
    const userProfessionCodesArr = user?.professions ?? [];

    const professionCodesOrder = [
      UserProfession.DN,
      UserProfession.RDH,
      UserProfession.DA,
      UserProfession.FD,
    ];

    const orderedProfessionCodesArr = userProfessionCodesArr.sort((a, b) => {
      const indexA = professionCodesOrder.indexOf(a);
      const indexB = professionCodesOrder.indexOf(b);

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    return orderedProfessionCodesArr.map((code) => UserProfession.getName(code));
  }, [user?.professions]);

  const handleInviteNewShift = () => {
    if (source && sourceToDropdownSource[source] && sourceToProCategory[source]) {
      fireNewInviteClicked({
        source: sourceToDropdownSource[source],
        proCategory: sourceToProCategory[source],
      });
    }

    if (shouldUseShiftRequestRedesign) {
      const professional = { id: user?.id, name: user?.name };
      if (filteredProfessions.length === 1) {
        history.push(routes.selectDates, {
          professionType: filteredProfessions[0].code,
          professionals: [professional],
        });
      } else {
        setShowProfessionPopup(true);
      }
      return;
    }

    history.push('/dashboard/multi-day/info', {
      professionals: [user?.id],
      professionTypesNames: getOrderedProfessionNames(),
      officeId: user?.officeId,
    });
  };

  useEffect(() => {
    if (isSuccessRequest && professionalId === user?.id) {
      dispatch(clearSuccess(actions));
      if (offers?.length) {
        history.push(`/invite-professional`, {
          professional: {
            id: user?.id,
            image: user?.image,
            name: user?.name,
            profession: user?.profession,
            isFavorite: user?.isFavorite,
            isRecommendedPros: user?.isRecommendedPros,
            isRecent: user?.isRecent,
          },
        });
      } else {
        dispatch(
          showMessage({
            title: 'No Upcoming Shifts',
            message: (
              <div
                style={{
                  textAlign: 'left',
                }}
              >
                Looks like there are no shifts that can be sent to this professional. This may
                happen because either:
                <br />
                <br />
                <ul>
                  <li>Your office does not have any shifts posted.</li>
                  <li>
                    This professional is unavailable or already booked for the dates you need
                    coverage.
                  </li>
                </ul>
              </div>
            ),
            popupProps: {
              rightButtonText: 'Close',
              rightButtonAction: () => {
                dispatch(clearSuccess(actions));
              },
              descriptionStyle: {
                marginBottom: 60,
                width: 420,
              },
            },
          }),
        );
      }
    }
  }, [isSuccessRequest, professionalId, dispatch, user, offers]);

  return (
    <>
      <DotsMenu
        dataCy="invite-menu-button"
        position={position}
        disabled={disabled}
        onValidate={onValidate}
        CustomMenuIcon={renderCustomMenuIcon}
        listener={() => {
          listener?.();
          fireEntryPointEvent();
        }}
      >
        <MenuItem
          dataCy="existing-shift"
          text="Existing Shift"
          onClick={handleInviteToExistingShift}
        />
        <MenuItem dataCy="new-shift" text="New Shift" onClick={handleInviteNewShift} />
      </DotsMenu>

      {showProfessionPopup && (
        <SelectProfessionTypePopup
          professions={filteredProfessions}
          onContinue={(professionCode) => {
            setShowProfessionPopup(false);
            history.push(routes.selectDates, {
              professionType: professionCode,
              professionals: [{ id: user?.id, name: user?.name }],
            });
          }}
          onClose={() => setShowProfessionPopup(false)}
        />
      )}
    </>
  );
};

const styles = {
  buttonStyle: {
    display: 'flex',
    height: 50,
    width: 50,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    backgroundColor: Colors.primary_500,
  },
  imgStyle: {
    width: 24,
    height: 24,
  },
};
